import React, { useMemo } from "react";
import { Panel } from "primereact/panel";
import { DataTypeEnum, FieldType } from "../../api/types/field-type";
import { ProfileFieldValue } from "../../api/types/crm/profile-fields";
import { GroupFieldSection } from "./GroupFieldSection";
import type { CollectedField } from "./GroupFieldSection";
import styles from "./NestedGroupRenderer.module.css";

interface NestedGroupRendererProps {
  groupFieldType: FieldType;
  profileId: string;
  existingFieldValues?: ProfileFieldValue[];
  depth?: number;
  parentFieldValueId?: string | null;
  onSaveSuccess?: () => void;
  onSaveError?: (error: string) => void;
  onRegisterCollector?: (
    groupFieldTypeId: string,
    collector: (enforceSortOrder?: number | null) => CollectedField[],
  ) => void;
  onRegisterSaver?: (
    groupFieldTypeId: string,
    saver: () => Promise<void>,
  ) => void;
  onUnsavedChange?: (groupId: string, hasUnsaved: boolean) => void;
}

export const NestedGroupRenderer: React.FC<NestedGroupRendererProps> = ({
  groupFieldType,
  profileId,
  existingFieldValues = [],
  depth = 0,
  parentFieldValueId,
  onSaveSuccess,
  onSaveError,
  onRegisterCollector,
  onRegisterSaver,
  onUnsavedChange,
}) => {
  // Get children from the fieldTypes array
  const children = groupFieldType.fieldTypes || [];

  // Separate children into groups and fields
  const childFields = children.filter(
    (child) => child.type !== DataTypeEnum.Group,
  );

  // Calculate indentation based on depth
  const indentStyle = {
    marginLeft: `${depth * 1.5}rem`,
  };

  // Get depth-specific CSS class
  const depthClass = styles[`depth${Math.min(depth, 3)}`] || "";

  // Only ROOT level (depth=0) supports multiple instances

  //

  // Check if this nested group has any filled data
  const hasFilledData = useMemo(() => {
    if (existingFieldValues.length === 0) return false;

    // Get all field IDs for child fields (direct children only)
    const childFieldIds = childFields.map((cf) => cf.id);

    // Check if any field values exist and have non-empty values
    return existingFieldValues.some((fv) => {
      if (!childFieldIds.includes(fv.fieldTypeId)) return false;

      // Check if value is non-empty
      const value = fv.value;
      if (value === null || value === undefined || value === "") return false;
      if (Array.isArray(value) && value.length === 0) return false;

      return true;
    });
  }, [existingFieldValues, childFields]);

  // If this is ROOT level, use GroupFieldSection for multiple instances
  // GroupFieldSection will handle both fields and nested groups
  if (depth === 0) {
    return (
      <div style={indentStyle} className={styles.nestedGroupContainer}>
        <GroupFieldSection
          groupFieldType={groupFieldType}
          childFieldTypes={children}
          profileId={profileId}
          existingFieldValues={existingFieldValues}
          parentFieldValueId={parentFieldValueId}
          onSaveSuccess={onSaveSuccess}
          onSaveError={onSaveError}
          onRegisterCollector={onRegisterCollector}
          onRegisterSaver={onRegisterSaver}
          onUnsavedChange={onUnsavedChange}
        />
      </div>
    );
  }

  //

  // Custom header template with depth indicator
  const headerTemplate = (
    <div className={styles.groupHeader}>
      <span className={styles.depthIndicator}></span>
      <span>{groupFieldType.name}</span>
    </div>
  );

  // If this group has nested groups or is nested itself, render with panel
  return (
    <div
      style={indentStyle}
      className={`${styles.nestedGroupContainer} ${depthClass}`}
    >
      <Panel
        header={headerTemplate}
        toggleable={true}
        className={`mb-3 ${styles.nestedGroupPanel} ${depthClass}`}
        collapsed={depth > 0 && !hasFilledData}
      >
        <div className={styles.nestedContent}>
          {depth > 0 && (
            <div className="mb-3">
              <GroupFieldSection
                groupFieldType={groupFieldType}
                childFieldTypes={children}
                profileId={profileId}
                existingFieldValues={existingFieldValues}
                parentFieldValueId={parentFieldValueId}
                onSaveSuccess={onSaveSuccess}
                onSaveError={onSaveError}
                onRegisterCollector={onRegisterCollector}
                onRegisterSaver={onRegisterSaver}
                onUnsavedChange={onUnsavedChange}
              />
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
};
